/**
 * Phone-number parsing shared by the onboarding steps.
 *
 * The voice agent may hand us a number in several shapes — a full international string
 * ("+918113947399" or "+1 811 394 7399"), or just a local number ("8113947399") with the
 * country code supplied separately. These helpers split it deterministically into a dial code
 * (e.g. "+91") + local number, so the country-code dropdown and the number field are always
 * populated correctly instead of dumping the whole thing into the number field.
 */

/** Default dial code for this product's audience (US small contractors). */
export const DEFAULT_DIAL_CODE = '+1';

/** Normalise a dial code to the "+<digits>" form the dropdown expects. */
export function normalizeDialCode(code: string): string {
  const digits = (code || '').replace(/[^\d]/g, '');
  return digits ? `+${digits}` : '';
}

/**
 * Split a phone string into { countryCode, number }.
 *
 * - Strips spaces, dashes, parentheses.
 * - If it starts with "+", matches the LONGEST known dial code (so "+91" wins over "+9"); if no
 *   known code matches, falls back to the leading 1–3 digits as the code.
 * - If it has no "+", returns countryCode "" (the caller applies its own default) and the digits
 *   as the local number.
 *
 * `dialCodes` is the list from the countries API (values like "+1", "+91"); order doesn't matter.
 */
export function splitPhone(
  input: string,
  dialCodes: string[] = [],
): { countryCode: string; number: string } {
  const cleaned = (input || '').replace(/[\s\-().]/g, '');
  if (!cleaned) return { countryCode: '', number: '' };

  if (cleaned.startsWith('+')) {
    const codes = dialCodes
      .map(normalizeDialCode)
      .filter(Boolean)
      .sort((a, b) => b.length - a.length); // longest first
    const match = codes.find((c) => cleaned.startsWith(c));
    if (match) return { countryCode: match, number: cleaned.slice(match.length) };
    // Unknown code but clearly international — take the leading 1–3 digits as the code.
    const m = cleaned.match(/^\+(\d{1,3})(\d+)$/);
    if (m) return { countryCode: `+${m[1]}`, number: m[2] };
    return { countryCode: '', number: cleaned.replace(/^\+/, '') };
  }

  return { countryCode: '', number: cleaned };
}
